import i18n from "general/localization/i18n";
import type { TFunction } from "i18next";
import { isNullish, isPlainObject, pickBy } from "remeda";

export const EMPTY_VALUE = "-";

/** Extracts string keys from a given object type */
export type StringKeys<T> = Extract<keyof T, string>;

export type MaybeString = string | null | undefined;

/** Check if a value is empty (null, undefined, or string with no content excluding whitespace) */
export const isEmptyValue = (
  value: unknown,
): value is null | undefined | string =>
  isNullish(value) || (typeof value === "string" && value.trim() === "");

/** Check if a value is not a value (null, undefined, or string with no content excluding whitespace, or a number that is NaN) */
export const isNonValue = (
  value: unknown,
): value is null | undefined | string | (number & { toString: () => "NaN" }) =>
  isEmptyValue(value) || (typeof value === "number" && Number.isNaN(value));

/** Check if a value is an array with no elements */
export const isEmptyArray = (value: unknown): value is [] =>
  Array.isArray(value) && value.length === 0;

/** Format a number for display in the active locale, trimming trailing zeros.
 * `maxDecimals` caps the fraction digits (default 2).
 * E.g. en: 1234.5 → "1,234.5"; fi: 1234.5 → "1 234,5". */
export function formatNumber(
  value: number | string,
  maxDecimals = 2,
  alwaysShowDecimals = false,
) {
  return new Intl.NumberFormat(i18n.language || "en", {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: alwaysShowDecimals ? maxDecimals : undefined,
  }).format(Number(value));
}

/** Display-safe variant of `formatNumber`: returns `EMPTY_VALUE` ("-") for
 * non-numeric/NaN input instead of a localized "NaN"/"epäluku" string. Use this
 * for UI output; use `formatNumber` only when the value is guaranteed numeric. */
export function formatDisplayNumber(
  value?: number | string | null,
  maxDecimals = 2,
  alwaysShowDecimals = false,
) {
  if (isEmptyValue(value) || Number.isNaN(value)) return EMPTY_VALUE;
  return formatNumber(value, maxDecimals, alwaysShowDecimals);
}

/** Like `formatDisplayNumber` but appends a unit suffix when there is a value to
 * show. The unit is omitted for empty/NaN values, so a missing value stays
 * `EMPTY_VALUE` (e.g. "-", never "- kg"). */
export function formatNumberWithUnit(
  value?: number | string | null,
  unit?: string | null,
  maxDecimals = 2,
  alwaysShowDecimals = false,
) {
  const formatted = formatDisplayNumber(value, maxDecimals, alwaysShowDecimals);
  return unit && formatted !== EMPTY_VALUE ? `${formatted} ${unit}` : formatted;
}

/** Substitutes path parameters in a URL template, e.g. buildUrl("/foo/:id", { id: 5 }) → "/foo/5" */
export const buildUrl = (
  template: string,
  params: Record<string, string | number>,
): string =>
  Object.entries(params).reduce(
    (url, [key, value]) => url.replace(`:${key}`, String(value)),
    template,
  );

/** Find item (or filter items) by key value from object, example usage: const item = entities.find(by("id", 123)); */
export const by = <T extends object>(key: keyof T, value: unknown) => {
  return (item: T) => item[key] === value;
};

/** Check if a key exists in an object (with type guarding) */
export const isKeyInObject = <T extends object>(
  key: unknown,
  obj: T,
): key is keyof T => {
  return typeof key === "string" && Object.hasOwn(obj, key);
};

/** Filter the inputted list of objects by a text search on given fields */
export function multiSearch<T extends object>(
  list: T[],
  fields: (keyof T)[],
  search: string,
): T[] {
  if (!list.length || !fields.length || !search.trim()) {
    return list;
  }

  const searchTerms = search.toLowerCase().trim().split(/\s+/);
  return list.filter((obj) =>
    searchTerms.every((term) =>
      fields.some((field) => {
        const val = obj[field];
        return val != null && String(val).toLowerCase().includes(term);
      }),
    ),
  );
}

/** Convert empty strings to null in an object */
export function convertEmptyStringsToNull(obj: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      value === "" ? null : value,
    ]),
  );
}

const commonNetworkErrorMessageSnippets = [
  "network error",
  "NetworkError",
  "Failed to fetch",
  "Load failed",
  "connection appears to be offline",
  "fetch failed",
  "request failed",
  "terminated",
];

/** Detects if the error is a network error (probably) */
export const isNetworkError = (error: Error) =>
  commonNetworkErrorMessageSnippets.some((msg) => error?.message.includes(msg));

/** Resolves the initial theme based on local storage or system preference */
export const resolveInitialTheme = (): string => {
  if (typeof window !== "undefined" && window.localStorage) {
    const storedPrefs = window.localStorage.getItem("heroui-theme");
    if (
      typeof storedPrefs === "string" &&
      ["light", "dark", "system"].includes(storedPrefs)
    ) {
      return storedPrefs;
    }
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

/** For cases where translation is not needed, e.g. tests or extraction of translation keys */
export const identityTranslator = ((key: string) => key) as TFunction;

/** Parses a composite ID of the form "id1-id2" into a tuple of two numbers. */
export function parseComposedId(
  composedId: string | undefined,
): [number, number] | [undefined, undefined] {
  if (!composedId) return [undefined, undefined];
  const parts = composedId.split("-");
  if (parts.length !== 2) return [undefined, undefined];
  const id1 = Number.parseInt(parts[0], 10);
  const id2 = Number.parseInt(parts[1], 10);
  if (Number.isNaN(id1) || Number.isNaN(id2)) return [undefined, undefined];
  return [id1, id2];
}

/** Deep merges two objects so that nullish values in the source are not overridden in the destination object. */
export function mergeDeepWithoutNullish(
  destination: unknown,
  source: unknown,
): unknown {
  if (!isPlainObject(source)) return undefined;
  const destinationObject = isPlainObject(destination) ? destination : {};
  const nonEmptyFormFields = pickBy(source, (value) => !isEmptyValue(value));
  Object.entries(nonEmptyFormFields).forEach(([key, value]) => {
    const mergedValue = isPlainObject(value)
      ? mergeDeepWithoutNullish(destinationObject[key], value)
      : value;
    nonEmptyFormFields[key] = mergedValue;
  });
  return { ...destinationObject, ...nonEmptyFormFields };
}
